// @ts-strict-ignore
import React, { memo } from "react";
import {
  AnimationBoxHeading,
  ContentBox,
  ContentRightSideBody,
  DownAnimation
} from "./SmallCard.styled";

import { ContentfulResponsiveImage } from "components/common/image/ContentfulResponsiveImage";
import type { CtaProps, SmallCardProps } from "types/prices";
import NextLink from "components/parts/NextLink";
import { getBodyV1Sizes } from "../../../../utils/common";
import { Heading } from "app/_components/components/Text/Heading";
import { Body } from "app/_components/components/Text/Body";
import { Display } from "app/_components/components/Text/Display";
import { Button } from "app/_components/components/Button";

function FadeInWhenVisibleHeading({ children }) {
  return (
    <AnimationBoxHeading
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true }}
      transition={{
        duration: 0.7,
        delay: 0.2,
        cubic: [0.25, 0.46, 0.45, 0.94]
      }}
      variants={{
        visible: { opacity: 1, y: 0 },
        hidden: { opacity: 0, y: -20 }
      }}
    >
      {children}
    </AnimationBoxHeading>
  );
}
function FadeInWhenVisibleDown({ children }) {
  return (
    <DownAnimation
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true }}
      transition={{
        duration: 0.7,
        delay: 0.2,
        cubic: [0.25, 0.46, 0.45, 0.94]
      }}
      variants={{
        visible: { opacity: 1, y: 0 },
        hidden: { opacity: 0, y: 20 }
      }}
    >
      {children}
    </DownAnimation>
  );
}

const SmallCard = (props: SmallCardProps) => {
  const { isLgTitle, subTitleSize } = props;
  const { title, coinFullName, subTitle, cta, image, imageMobile } =
    props?.smallCard || {};
  const renderButton = ({ name }: { name: string }) => {
    return (
      <Button variant="tertiary" className="bg-white">
        {name}
      </Button>
    );
  };

  return (
    <ContentBox className={props?.className}>
      <FadeInWhenVisibleHeading>
        {isLgTitle ? (
          <Display
            size="xs"
            variant="marketing"
            as="h2"
            className="mb-4 text-white"
          >
            {(title || "").replace(`{{coinName}}`, coinFullName)}
          </Display>
        ) : (
          <Heading size="md" className="mb-2 text-white">
            {(title || "").replace(`{{coinName}}`, coinFullName)}
          </Heading>
        )}

        <Body
          size={getBodyV1Sizes(subTitleSize)}
          bold
          className="!mb-4 text-white"
        >
          {subTitle}
        </Body>
        {cta &&
          cta.length > 0 &&
          cta.map((data: CtaProps, index: number) => {
            if (data?.link) {
              return (
                <NextLink href={data?.link} id={`button-${index}`} key={index}>
                  {renderButton({ name: data?.name })}
                </NextLink>
              );
            } else {
              return (
                <React.Fragment key={index}>
                  {renderButton({ name: data?.name })}
                </React.Fragment>
              );
            }
          })}
      </FadeInWhenVisibleHeading>
      {imageMobile?.url && (
        <ContentRightSideBody>
          <FadeInWhenVisibleDown>
            <ContentfulResponsiveImage
              alt={imageMobile?.altText}
              sizes={[
                {
                  media: "(min-width: 0)",
                  src: imageMobile?.url,
                  height: imageMobile?.height || 126,
                  width: imageMobile?.width || 201
                }
              ]}
            />
          </FadeInWhenVisibleDown>
        </ContentRightSideBody>
      )}
      {image?.url && (
        <ContentRightSideBody>
          <FadeInWhenVisibleDown>
            <ContentfulResponsiveImage
              alt={image?.altText}
              sizes={[
                {
                  media: "(min-width: 0)",
                  src: image?.url,
                  height: image?.height || 126,
                  width: image?.width || 201
                }
              ]}
            />
          </FadeInWhenVisibleDown>
        </ContentRightSideBody>
      )}
    </ContentBox>
  );
};

export default memo(SmallCard);
